import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { MissionType } from "@/types/missions"

/**
 * TypeSwitchConfirmDialog — UX Spec §1 § Edge flow.
 *
 * When the user changes the mission type AND has already entered any
 * type-specific data (`promo_code` or `ecommerce_url` for promotional),
 * we ask before clearing those fields. Title / brief / shared fields
 * always survive.
 *
 * Parent (MissionForm) owns `pendingType` state — when non-null, the
 * dialog is open. Confirm fires `onConfirm(pendingType)`; Cancel fires
 * `onCancel()`. The dialog has no internal state.
 *
 * A11y: shadcn Dialog provides focus trap + Esc + aria-labelledby.
 */
interface TypeSwitchConfirmDialogProps {
  /** When non-null, dialog is open and showing this incoming type. */
  pendingType: MissionType | null
  /** Confirm — caller clears type-specific fields + writes the new type. */
  onConfirm: () => void
  /** Cancel — caller leaves the type unchanged. */
  onCancel: () => void
}

export function TypeSwitchConfirmDialog({
  pendingType,
  onConfirm,
  onCancel,
}: TypeSwitchConfirmDialogProps) {
  const { t } = useTranslation()
  const open = pendingType !== null

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <DialogContent
        data-testid="type-switch-confirm-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>
            {t("missions.form.type_switch_confirm.title")}
          </DialogTitle>
          <DialogDescription>
            {t("missions.form.type_switch_confirm.body")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="type-switch-cancel"
          >
            {t("missions.form.type_switch_confirm.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            data-testid="type-switch-confirm"
          >
            {t("missions.form.type_switch_confirm.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
